import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Info, AlertCircle } from 'lucide-react';
import { SaleFormData } from '../../types/Sales';
import { Product } from '../../types/Product';

const requiredNumber = (min: number, msg: string) =>
  z.number({ message: 'Geçerli bir sayı girin' }).min(min, msg);

const saleSchema = z.object({
  productId: requiredNumber(1, 'Ürün seçilmelidir'),
  quantitySold: requiredNumber(1, 'Miktar 1 veya daha büyük olmalıdır'),
  totalEarning: requiredNumber(0, 'Toplam tutar 0 veya daha büyük olmalıdır'),
  saleDate: z.string().min(1, 'Satış tarihi gereklidir'),
});

interface SalesFormProps {
  initialData?: Partial<SaleFormData>;
  onSubmit: (data: SaleFormData) => void;
  products: Product[];
  loading?: boolean;
  formRef?: React.RefObject<HTMLFormElement | null>;
}

export const SalesForm: React.FC<SalesFormProps> = ({
  initialData,
  onSubmit,
  products,
  loading,
  formRef,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<SaleFormData>({
    resolver: zodResolver(saleSchema),
    defaultValues: initialData || {
      productId: 0,
      quantitySold: 1,
      totalEarning: 0,
      saleDate: new Date().toISOString().slice(0, 16),
    },
  });

  const selectedProductId = watch('productId');
  const quantitySold = watch('quantitySold');
  const [unitPrice, setUnitPrice] = useState(0);

  const selectedProduct = products.find((p) => p.id === selectedProductId);

  // Set default unit price when product is selected
  useEffect(() => {
    if (selectedProduct) {
      const price = selectedProduct.retailPrice; // Satış fiyatı (selling price)
      setUnitPrice(price);
      const total = quantitySold * price;
      setValue('totalEarning', total);
    }
  }, [selectedProduct, quantitySold, setValue]);

  // Calculate total earning when quantity or price changes
  useEffect(() => {
    if (selectedProduct) {
      const total = quantitySold * unitPrice;
      setValue('totalEarning', total);
    }
  }, [quantitySold, unitPrice, selectedProduct, setValue]);

  // Set default sale date
  useEffect(() => {
    if (!initialData?.saleDate) {
      setValue('saleDate', new Date().toISOString().slice(0, 16));
    }
  }, [setValue, initialData]);

  const stockAvailable = selectedProduct ? selectedProduct.quantity : 0;
  const stockError = selectedProduct && quantitySold > stockAvailable;

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        {/* Row 1 */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">
              Ürün <span className="text-error">*</span>
            </label>
            <select
              {...register('productId', { valueAsNumber: true })}
              value={selectedProductId || ''}
              onChange={(e) => {
                setValue('productId', Number(e.target.value));
              }}
              disabled={loading}
              className={`input ${errors.productId ? 'border-error focus:ring-error' : ''}`}
            >
              <option value={0}>Ürün Seçin</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} {product.brand && `- ${product.brand}`} (Stok: {product.quantity})
                </option>
              ))}
            </select>
            {errors.productId && (
              <p className="mt-1.5 text-xs text-error flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.productId.message}
              </p>
            )}
            {selectedProduct && (
              <div className="mt-2 p-2.5 rounded-lg bg-accent/10 border border-accent/20 flex items-center gap-2">
                <Info className="w-4 h-4 text-accent flex-shrink-0" />
                <p className="text-xs text-foreground-secondary">
                  Mevcut Stok: <span className="font-medium text-foreground">{stockAvailable}</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">
              Satılan Miktar <span className="text-error">*</span>
            </label>
            <input
              type="number"
              {...register('quantitySold', { valueAsNumber: true })}
              onWheel={(e) => e.currentTarget.blur()}
              disabled={loading}
              className={`input ${errors.quantitySold || stockError ? 'border-error focus:ring-error' : ''}`}
            />
            {(errors.quantitySold || stockError) && (
              <p className="mt-1.5 text-xs text-error flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.quantitySold?.message || `Yetersiz stok! Mevcut: ${stockAvailable}`}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">
              Birim Fiyat <span className="text-error">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={unitPrice}
              onChange={(e) => {
                const price = parseFloat(e.target.value) || 0;
                setUnitPrice(price);
                setValue('totalEarning', quantitySold * price);
              }}
              onWheel={(e) => e.currentTarget.blur()}
              disabled={loading || !selectedProduct}
              className={`input ${errors.totalEarning ? 'border-error focus:ring-error' : ''}`}
            />
          </div>
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">
              Toplam Tutar <span className="text-error">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              {...register('totalEarning', { valueAsNumber: true })}
              onWheel={(e) => e.currentTarget.blur()}
              disabled
              className="input bg-background-tertiary cursor-not-allowed"
            />
            {errors.totalEarning && (
              <p className="mt-1.5 text-xs text-error">{errors.totalEarning.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">
              Satış Tarihi <span className="text-error">*</span>
            </label>
            <input
              type="datetime-local"
              {...register('saleDate')}
              disabled={loading}
              className={`input ${errors.saleDate ? 'border-error focus:ring-error' : ''}`}
            />
            {errors.saleDate && (
              <p className="mt-1.5 text-xs text-error">{errors.saleDate.message}</p>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};
